import React, { Fragment } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import {polyglot} from '../../../index'
import {
  Crosshair, DiscreteColorLegend, FlexibleWidthXYPlot,
  HorizontalGridLines, LineMarkSeries, LineSeries, MarkSeries, VerticalGridLines, XAxis, YAxis
} from 'react-vis'
import Highlight from './highlights/Highlight'
import 'react-vis/dist/style.css'
import './style.css'
import SectionTitle from '../SectionTitle/SectionTitle'

export default class MyGraphics extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      lastDrawLocation: null,
      data: [],
      crosshairValues: [],
      normalizedValues: false
    }
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onNearestXY = this.onNearestXY.bind(this)
    this.legendItemClickHandler = this.legendItemClickHandler.bind(this)
  }
  componentWillMount () {
    const tempData = this.state.data
    let hasDifferentScales = false
    this.props.data.forEach(element => {
      if (element.isDifferentScale) {
        hasDifferentScales = true
      }
    })
    this.props.data.forEach((element, index) => {
      tempData[index] = this.transformData(element.data, hasDifferentScales)
    })
    const itemsForLegend = this.getItemsForLegend()
    this.setState({
      data: tempData,
      normalizedValues: hasDifferentScales,
      itemsForLegend
    })
  }

  onMouseLeave () {
    this.setState({
      crosshairValues: []
    })
  }

  onNearestXY (value, {index}) {
    this.setState({
      crosshairValues: this.state.data.map(d => d[index])
    })
  }

  transformData (data, needNormalization) {
    const result = []
    const maxVal = this.getMaxValueForObject(data, 'y')
    const minVal = this.getMinValueForObject(data, 'y')
    Object.keys(data).forEach(key => {
      const element = data[key]
      let yValue = element.y
      if (needNormalization) {
        yValue = normalization(element.y, minVal, maxVal)
      }
      result.push({x: element.x, y: yValue, original: element.y})
    })
    return result
  }

  getMaxValueForObject (objects, property) {
    const res = []
    Object.keys(objects).forEach(key => {
      const element = objects[key]
      res.push(element[property])
    })
    return Math.max(...res)
  }

  getMinValueForObject (objects, property) {
    const res = []
    Object.keys(objects).forEach(key => {
      const element = objects[key]
      res.push(element[property])
    })
    return Math.min(...res)
  }

  getDataForLine (index) {
    return this.state.data[index]
  }

  renderLine (line, index) {
    if (line.type === 'line') {
      return <LineSeries
        opacity={this.state.itemsForLegend[index].disabled ? 0 : 1}
        key={line.title}
        onNearestXY={!line.noHover ? this.onNearestXY : null}
        color={line.color}
        getNull={(d) => d.y !== null}
        xType={line.xType}
        curve={'curveMonotoneX'}
        data={this.getDataForLine(index)}
      />
    } else if (line.type === 'mark') {
      return <MarkSeries
        opacity={this.state.itemsForLegend[index].disabled ? 0 : 1}
        key={line.title}
        color={line.color}
        onNearestXY={!line.noHover ? this.onNearestXY : null}
        getNull={(d) => d.y !== null}
        xType={line.xType}
        data={this.getDataForLine(index)}
      />
    } else {
      return <LineMarkSeries
        opacity={this.state.itemsForLegend[index].disabled ? 0 : 1}
        key={line.title}
        color={line.color}
        onNearestXY={!line.noHover ? this.onNearestXY : null}
        getNull={(d) => d.y !== null}
        xType={line.xType}
        curve={'curveMonotoneX'}
        data={this.getDataForLine(index)}
      />
    }
  }

  getTickTotalNumber () {
    let maxValue = 0
    this.props.data.forEach(element => {
      maxValue = Math.max(maxValue, element['data'].length)
    })
    return Math.min(maxValue, this.maxPossibleTickNumber())
  }

  maxPossibleTickNumber () {
    return 20
  }

  addYAxis () {
    let margin = 25
    let i = 1
    return this.props.data.map((element, index) => {
      const isElementHidden = this.state.itemsForLegend[index].disabled ? 0 : 1
      const minValue = this.getMinValueForObject(this.state.data[index], 'original')
      const maxValue = this.getMaxValueForObject(this.state.data[index], 'original')
      if (index === 0) {
        const tickFormat = (d) => {
          if (this.state.normalizedValues) {
            return originalValue(d, minValue, maxValue)
          } else return d
        }
        return <YAxis className={'no-select'}
          style={{
            line: {opacity: isElementHidden},
            ticks: {opacity: isElementHidden},
            text: {opacity: isElementHidden},
            title: {opacity: isElementHidden}
          }}
          key={index}
          title={polyglot.t(`staticFields.graphics.${element.title}`)}
          tickFormat={d => tickFormat(d)} />
      } else if (element.isDifferentScale) {
        const side = i % 2 === 0 ? 'left' : 'right'
        const positioningProps = {[side]: (margin * i++), orientation: side}
        return <YAxis className={'no-select'}
          style={{
            line: {opacity: isElementHidden},
            ticks: {opacity: isElementHidden},
            text: {opacity: isElementHidden},
            title: {opacity: isElementHidden}
          }}
          {...positioningProps}
          key={index}
          tickFormat={d => `${originalValue(d, minValue, maxValue)}`}
          title={polyglot.t(`staticFields.graphics.${element.title}`)} />
      } else return null
    })
  }

  getItemsForLegend () {
    const items = []
    this.props.data.forEach(element => {
      items.push({title: polyglot.t(`staticFields.graphics.${element.title}`), color: element.color, disabled: false})
    })
    return items
  }

  legendItemClickHandler (item) {
    const {itemsForLegend} = this.state
    item.disabled = !item.disabled
    this.setState({
      itemsForLegend
    })
  }

  render () {
    const { lastDrawLocation } = this.state
    return <Fragment>
      <div className='center'>
        {this.props.header
          ? <SectionTitle title={this.props.header} size={'h4'} />
          : null
        }
        <DiscreteColorLegend
          onItemClick={this.legendItemClickHandler}
          orientation='horizontal'
          items={this.state.itemsForLegend}
        />
      </div>
      <FlexibleWidthXYPlot xDomain={lastDrawLocation && [lastDrawLocation.left, lastDrawLocation.right]} height={this.props.height} onMouseLeave={this.onMouseLeave} margin={{bottom: 100, left: 50, right: 50}} className={'graphic-window'}>
        <VerticalGridLines />
        <HorizontalGridLines />
        {this.props.data.map((element, index) => {
          return this.renderLine(element, index)
        })}
        {this.props.type === 'date'
          ? <XAxis className={'no-select'} title={polyglot.t('staticFields.commonFields.date')} tickLabelAngle={-45} tickTotal={this.getTickTotalNumber()} tickFormat={v => `${moment(new Date(v)).format('L')}`} />
          : <XAxis className={'no-select'} title='Tipo por definir' tickTotal={this.getTickTotalNumber()} />
        }
        {this.state.crosshairValues.length > 0
          ? <Crosshair values={this.state.crosshairValues}>
            <div className={'crosshair'}>
              {this.props.type === 'date'
                ? <h5>{moment(new Date(this.state.crosshairValues[0].x)).format('LL')}</h5>
                : <h5>por definir</h5>
              }
              {this.props.data.map((element, index) => {
                const isElementHidden = this.state.itemsForLegend[index].disabled
                if (!isElementHidden && !element.noHover) {
                  return <p key={index}>{`${polyglot.t(`staticFields.graphics.${element.title}`)}: ${this.state.crosshairValues[index].original}`}</p>
                } else return null
              })}
            </div>
          </Crosshair>
          : null
        }
        <Highlight color={'red'} onBrushEnd={(area) => {
          this.setState({
            lastDrawLocation: area
          })
        }} />
        {this.addYAxis()}
      </FlexibleWidthXYPlot>
    </Fragment>
  }
}

function normalization (value, min, max) {
  return ((value - min) / (max - min))
}

function originalValue (normalizedValue, min, max) {
  return Math.trunc(min + (normalizedValue * (max - min)))
}

MyGraphics.propTypes = {
  title: PropTypes.string,
  type: PropTypes.oneOf(['date', 'numeric', 'ordinal']),
  height: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      noHover: PropTypes.bool.isRequired,
      ignoreX: PropTypes.bool.isRequired,
      xType: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['line', 'mark', 'lineMark']),
      data: PropTypes.arrayOf(PropTypes.shape({
        x: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
        y: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      }))
    }).isRequired
  )
}

MyGraphics.defaultProps = {
  height: 600
}



// WEBPACK FOOTER //
// ./src/components/common/MyGraphics/MyGraphics.js